import { createRoot } from 'react-dom/client';
import { Button, Text, UNSTABLE_Toast as AriaToast, UNSTABLE_ToastContent as ToastContent, UNSTABLE_ToastQueue as ToastQueue, UNSTABLE_ToastRegion as ToastRegion } from 'react-aria-components';
import { X } from 'lucide-react';
import { tr } from '../../../i18n/runtime';
import { FM } from './messages';
import './styles';

export interface ToastOptions {
  message: string;
  /** Кнопка «Отменить» (подпись из каталога): нажатие зовёт `onUndo` и закрывает уведомление. */
  undo?: { onUndo: () => void; label?: string };
  tone?: 'info' | 'error';
  /** Мс до автозакрытия. Для уведомления с отменой по умолчанию 8000, иначе 5000; `0` — не закрывать само. */
  timeout?: number;
}

interface ToastItem {
  message: string;
  tone: 'info' | 'error';
  undoLabel?: string;
  onUndo?: () => void;
  closeLabel: string;
}

const queue = new ToastQueue<ToastItem>({ maxVisibleToasts: 3 });
let hosted = false;

function Host() {
  return (
    <ToastRegion queue={queue} className="fm-toasts" aria-label={tr(FM.toastRegion)}>
      {({ toast }) => (
        <AriaToast toast={toast} className="fm-toast" data-tone={toast.content.tone}>
          <ToastContent className="fm-toast__text">
            <Text slot="title">{toast.content.message}</Text>
          </ToastContent>
          {toast.content.onUndo ? (
            <Button
              className="fm-toast__btn"
              onPress={() => {
                toast.content.onUndo?.();
                queue.close(toast.key);
              }}
            >
              {toast.content.undoLabel}
            </Button>
          ) : null}
          <Button slot="close" className="fm-toast__close" aria-label={toast.content.closeLabel}>
            <X size={16} aria-hidden />
          </Button>
        </AriaToast>
      )}
    </ToastRegion>
  );
}

/** Область уведомлений монтируется сама при первом показе: страницам не нужно её подключать. */
function ensureHost(): void {
  if (hosted || typeof document === 'undefined') return;
  hosted = true;
  const node = document.createElement('div');
  node.setAttribute('data-fm-toast-host', '');
  document.body.appendChild(node);
  createRoot(node).render(<Host />);
}

/**
 * Уведомление внизу экрана. Для «сделано, но можно вернуть»: правка сохранена
 * сразу, а «Отменить» остаётся на несколько секунд. Таймер встаёт на паузу при
 * наведении и фокусе; диктору уведомление читается как статус.
 *
 *   toast.show({ message: 'Окно сдвинуто на 7 дней', undo: { onUndo: revert } });
 */
export const toast = {
  show(options: ToastOptions): string {
    ensureHost();
    const timeout = options.timeout ?? (options.undo ? 8000 : 5000);
    return queue.add(
      {
        message: options.message,
        tone: options.tone ?? 'info',
        undoLabel: options.undo ? (options.undo.label ?? tr(FM.toastUndo)) : undefined,
        onUndo: options.undo?.onUndo,
        closeLabel: tr(FM.toastClose),
      },
      timeout > 0 ? { timeout } : undefined,
    );
  },
  close(key: string): void {
    queue.close(key);
  },
};
